import { Identify, Types, add, identify, initAll, reset, setGroup, setUserId, track } from '@amplitude/unified';
import { AccountDefinition, Features, ModuleNames } from '../../../common/model/account-definition';
import { TrialTrackingEnvironment } from '../../../../environments/trial-tracking-config';

// The guide asks us to agree this property name with Amplitude before instrumenting. Renaming this
// constant is the only change needed if that discussion settles on something else.
const applicationPropertyName = 'application';
const applicationPropertyValue = 'Landlord';

// A customer is the license owner in Hogia terms. This is sent as an Amplitude group rather than a
// user property because the relationship belongs to the customer dimension, not to the user.
const licenseOwnerGroupType = 'customer';

export class AmplitudeService {
  private static _initialized = false;
  private static _environment: TrialTrackingEnvironment;

  public static initialize(apiKey: string, environment: TrialTrackingEnvironment): void {
    if (AmplitudeService._initialized) return;

    AmplitudeService._environment = environment;

    // Enrichment plugins have to be registered before init, otherwise the events collected during
    // startup are not stamped. Calls made before init are queued and applied once init completes.
    add(AmplitudeService.applicationPropertyPlugin());

    // initAll starts Analytics, Session Replay, Experiment and Guides & Surveys behind one call.
    // It is deliberately not awaited so that it never delays application startup.
    initAll(apiKey, {
      serverZone: 'EU',
      analytics: {
        logLevel: Types.LogLevel.None,
        autocapture: {
          attribution: true,
          fileDownloads: true,
          formInteractions: true,
          pageViews: true,
          sessions: true,
          elementInteractions: {
            // Regex instances are used rather than the { pattern, description } form because that
            // form is compiled without the global flag and masks only the first match per element.
            maskTextRegex: [
              /\b(?:\d{8}|\d{6})[-+]\d{4}\b/g,
              /\b(?:19|20)\d{10}\b/g
            ]
          },
          frustrationInteractions: true,
          webVitals: true
          // networkTracking stays off because it captures request URLs and needs separate review.
        }
      },
      sessionReplay: {
        sampleRate: 0,
        debugMode: false,
        useWebWorker: true,
        privacyConfig: {
          // Inputs are masked by the plugin. Sensitive non-input content is marked with
          // data-recorders-suppress, which adds both Amplitude masking mechanisms.
          defaultMaskLevel: 'medium'
        }
      }
    }).catch(() => undefined);

    AmplitudeService._initialized = true;
  }

  // Every public call is guarded. Without the guard, local runs and unit tests would queue calls
  // forever because initialize() is intentionally limited to test and production.
  public static identifyUser(userContext: AccountDefinition): void {
    if (!AmplitudeService._initialized) return;

    setUserId(userContext.id);
    identify(AmplitudeService.userProperties(userContext));
    AmplitudeService.setLicenseOwner(userContext.systemOwnerId);
  }

  public static updateUserProperties(userContext: AccountDefinition): void {
    if (!AmplitudeService._initialized) return;

    identify(AmplitudeService.userProperties(userContext));
  }

  public static setLicenseOwner(customerId: string): void {
    if (!AmplitudeService._initialized) return;

    setGroup(licenseOwnerGroupType, customerId);
  }

  public static track(eventName: string, properties?: Record<string, unknown>): void {
    if (!AmplitudeService._initialized) return;

    track(eventName, properties);
  }

  public static resetUser(): void {
    if (!AmplitudeService._initialized) return;

    reset();
  }

  private static userProperties(userContext: AccountDefinition): Identify {
    const userProperties = new Identify();

    userProperties.set('environment', AmplitudeService._environment);
    userProperties.set('is_internal', AmplitudeService.isInternalEmail(userContext.email));
    userProperties.set('can_manage_users', userContext.hasAccess(ModuleNames.System, Features.Admin));
    userProperties.set('can_manage_billing', userContext.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices));
    userProperties.set('can_see_customer_account', userContext.hasAccess(ModuleNames.System, Features.Overview));
    userProperties.set('has_any_permission', userContext.modules.length > 0 || userContext.features.length > 0);
    userProperties.set('language', AmplitudeService.currentLanguage());

    return userProperties;
  }

  private static isInternalEmail(email: string): boolean {
    const domain = email?.toLowerCase().split('@')[1];
    return !!domain && domain.includes('hogia');
  }

  private static currentLanguage(): string {
    return localStorage.getItem('lang') ?? 'sv';
  }

  private static applicationPropertyPlugin(): Types.EnrichmentPlugin {
    return {
      name: 'hogia-application-property',
      type: 'enrichment',
      execute: async (event: Types.Event): Promise<Types.Event> => {
        event.event_properties = {
          ...event.event_properties,
          [applicationPropertyName]: applicationPropertyValue
        };
        return event;
      }
    };
  }
}
